/**
 * H23 sect dance runner. Real time comes in through setNow (dayReset.applyRealDay, once a minute) and is carried
 * forward by world step time in between. Members dancing in their own sect map get the ledger's `sectDance` source
 * (school neigong only). A notice goes out 10 minutes before. The group-dance rules (data/groupDance.ts) are reused
 * by the client ring; this piece is the schedule and the reward.
 */
import { SECT_DANCE, sectDanceState } from '../data/sectDance';
import { SECT_TABLE } from '../data/layouts/sects';
import type { SystemHost, WorldSystem } from './types';

const fmt = (ms: number): string => `${Math.max(1, Math.ceil(ms / 60_000))} นาที`;

export class SectDanceSystem implements WorldSystem {
  readonly name = 'sectDance';
  private now = Number.NaN;
  private noticed = -1;
  constructor(private readonly h: SystemHost) {}

  realDay(_day: number, nowMs: number): void {
    this.setNow(nowMs);
  }
  setNow(ms: number): void {
    this.now = ms;
  }

  /** The player's sect id when they are a member, else null. */
  private sect(): string | null {
    return this.h.world.progress.school ?? null;
  }

  /** Line for the daily keeper / today panel (members only). */
  line(): string | null {
    const id = this.sect();
    if (!id || Number.isNaN(this.now)) return null;
    const st = sectDanceState(id, this.now);
    const name = SECT_TABLE.find((r) => r.id === id)?.name ?? id;
    return st.phase === 'open'
      ? `• รำสำนัก ${name}: เปิดอยู่ที่ลานสำนัก อีก ${fmt(st.endsIn)} (กำลังภายในสำนักฝึกเพิ่ม)`
      : `• รำสำนัก ${name}: อีก ${fmt(st.startsIn)} (ทุก 4 ชั่วโมง ที่ลานสำนัก)`;
  }

  playerStep(dt: number): void {
    if (Number.isNaN(this.now)) return;
    this.now += dt * 1000;
    const w = this.h.world;
    const id = this.sect();
    const boost = w.sys.cultivation.boost;
    if (!id) {
      boost.clear('sectDance');
      return;
    }
    const st = sectDanceState(id, this.now);
    if (st.phase === 'notice') {
      const start = Math.round(this.now + st.startsIn);
      if (this.noticed !== start) {
        this.noticed = start;
        this.h.notice(`อีก ${fmt(st.startsIn)} สำนักจะรำสำนัก ไปรวมตัวที่ลานสำนัก`);
      }
    }
    const here = (w.progress.location as string) === `sect-${id}`;
    if (st.phase === 'open' && here) boost.set('sectDance', SECT_DANCE.bonus, st.endsIn / 1000);
    else boost.clear('sectDance');
  }
}
